import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  type TableState,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useMemo } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import Pagination from "common/components/ui/Pagination";
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "common/components/ui/Table";
import { getPaginationInfoText } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  onRowClick?: (rowValue: TData) => void;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick = null,
  isLoading,
}: DataTableProps<TData, TValue>) {
  const is2xlDesktop = useTwBreakpoint("2xl");

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });

  const [sortBy, setSortBy] = useQueryParam(
    "sortBy",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [sortOrder, setSortOrder] = useQueryParam(
    "sortOrder",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const sorting: SortingState = useMemo(() => {
    if (!sortBy || !sortOrder) return [];

    return [{ id: sortBy, desc: sortOrder === "desc" }];
  }, [sortBy, sortOrder]);

  const pageSize = is2xlDesktop ? 14 : 7;

  const state: Partial<TableState> = {
    sorting,
    pagination: { pageIndex: isLoading ? 0 : page, pageSize },
  };

  const reactTableOptions: TableOptions<TData> = {
    data: isLoading || !data ? new Array(pageSize).fill({}) : data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      if (newSorting.length === 0) {
        // Clear sorting
        setSortBy("");
        setSortOrder("");
      } else {
        // Set new sorting
        const sort = newSorting[0];
        setSortBy(sort.id);
        setSortOrder(sort.desc ? "desc" : "asc");
      }
    },
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false,
    onPaginationChange: (updaterOrValue) => {
      setPage(
        typeof updaterOrValue === "function"
          ? updaterOrValue({ pageIndex: page, pageSize: 7 }).pageIndex
          : updaterOrValue.pageIndex,
      );
    },
    state,
  };

  const {
    getState,
    getHeaderGroups,
    getRowModel,
    setPageIndex,
    getRowCount,
    firstPage,
  } = useReactTable(reactTableOptions);

  if (getRowModel().rows.length === 0 && getRowCount() !== 0 && !isLoading) {
    firstPage();
  }

  const itemCount = data ? data.length : 0;

  return (
    <>
      <Table className="mb-auto">
        <TableHeader>
          {getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </tr>
          ))}
        </TableHeader>
        <tbody>
          {getRowModel().rows.map((row) => (
            <TableRow
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row.original);
                }
              }}
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              className={clsx({
                "hover:shadow-none hover:cursor-auto": !onRowClick,
              })}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>

      <div className="flex flex-col-reverse items-center justify-between gap-4 lg:flex-row mt-6">
        <span className="text-xs text-black-600">
          {getPaginationInfoText(page, pageSize, itemCount)}
        </span>
        <Pagination
          setPage={setPageIndex}
          selectedIndex={getState().pagination.pageIndex}
          pageSize={pageSize}
          itemCount={itemCount}
          isLastPageHidden={false}
        />
      </div>
    </>
  );
}
